import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { TIME_SERIES } from 'reporting-data/constants/configTypes';
import { CRM_OBJECT, DEALS, GOAL_TARGET } from 'reporting-data/constants/dataTypes';
import { SUM } from 'reporting-data/constants/metricTypes';
import { DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES } from 'reporting-data/constants/objectCoordinates';
import { EQ, IN } from 'reporting-data/constants/operators';
import { REPORT_BUILDER_ACCESS_SCOPE } from 'reporting-data/constants/scopes';
import { getCustomFilters, getDataType, getDimensions, getFilterGroups, getObjectTypeId, getReportProcessors, getUseFiscalYearInAggregation, isTimeSeriesReport } from 'reporting-data/report/configReportGetters';
import { getReportChartType } from 'reporting-data/report/reportGetters';
import { isConfigReport, isCustomWidgetReport, isMultiConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { customWidgetTypeIncludes } from 'reporting-ui-components/components/customWidgets/utils';
import { XY } from 'reporting-visualizations/constants/visualizationTypes';
import { XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { GOAL_EDITOR_EXCLUDED_CHART_TYPES, GOAL_EDITOR_EXCLUDED_CUSTOM_WIDGETS, GOAL_EDITOR_MAX_REPORT_DIMENSIONS, STATIC_LINE_SUPPORTED_CHART_TYPES } from './constants';
import { ADDITIONAL_OBJECT_TYPES_IN_SRV_GATE } from '../../../constants/gates';
// @ts-expect-error ts-migrate(7016)
import { AMOUNT_IN_HOME_CURRENCY_PROPERTIES } from 'reporting-data/properties/constants/multiCurrencyProperties';
export function isStaticReferenceLineSupportedByReport(report) {
  if (!isConfigReport(report) || isCustomWidgetReport(report)) {
    return false;
  }
  const reportChartType = getReportChartType(report);
  const isSupportedChartType = STATIC_LINE_SUPPORTED_CHART_TYPES.includes(reportChartType);
  return isTimeSeriesReport(report) && isSupportedChartType;
}
export function isGoalsSupportedByReport(report, gates, scopes) {
  if (!isConfigReport(report)) {
    return false;
  }
  const reportDataType = getDataType(report);
  const reportDimensions = getDimensions(report);
  const reportChartType = getReportChartType(report);
  const reportObjectTypeId = getObjectTypeId(report);
  const isSupportedChartType = reportChartType && !GOAL_EDITOR_EXCLUDED_CHART_TYPES.includes(reportChartType);
  const isSupportedWidget = !customWidgetTypeIncludes(report, GOAL_EDITOR_EXCLUDED_CUSTOM_WIDGETS);
  return scopes.includes(REPORT_BUILDER_ACCESS_SCOPE) && (reportDataType === DEALS || reportObjectTypeId === DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES.get(DEALS) || gates.includes(ADDITIONAL_OBJECT_TYPES_IN_SRV_GATE)) && isTimeSeriesReport(report) && isSupportedChartType && isSupportedWidget && reportDimensions.size === GOAL_EDITOR_MAX_REPORT_DIMENSIONS;
}
export const getValueFromCustomFilters = (filters, property, operator, onlyReturnFirstListValue = false) => {
  if (!filters) {
    return undefined;
  }
  const filterObject = filters.find(filter => filter.get('property') === property && filter.get('operator') === operator);
  if (!filterObject) {
    return undefined;
  }
  return operator === EQ ? filterObject.get('value') : onlyReturnFirstListValue ? filterObject.getIn(['values', 0]) : filterObject.get('values');
};
export const getTargetAmountProperty = (kpiUnitType, kpiPropertyName) => {
  if (kpiUnitType === 'currency' && AMOUNT_IN_HOME_CURRENCY_PROPERTIES.includes(kpiPropertyName)) {
    return 'hs_target_amount_in_home_currency';
  }
  return 'hs_target_amount';
};
export const getGoalReportLayer = (goal, reportConfig, disaggrregatedGoals) => {
  const {
    kpiPropertyName,
    datePropertyName,
    kpiUnitType,
    milestone,
    goalType,
    userAssigneeIdToNameMap,
    teamAssigneeIdToNameMap,
    groupCorrelationUuids
  } = goal;
  const targetAmount = getTargetAmountProperty(kpiUnitType, kpiPropertyName);
  const frequency = reportConfig.getIn(['config', 'frequency']);
  // When ungated to disaggregated goals we need to filter by group correlation uuid
  let goalCustomFilters = disaggrregatedGoals ? ImmutableList([ImmutableMap({
    operator: EQ,
    value: groupCorrelationUuids && groupCorrelationUuids[0],
    property: 'hs_group_correlation_uuid'
  }), ImmutableMap({
    operator: IN,
    values: ImmutableList([kpiUnitType]),
    property: 'hs_kpi_unit_type'
  })]) : ImmutableList([ImmutableMap({
    operator: EQ,
    value: kpiPropertyName,
    property: 'hs_kpi_property_name'
  }), ImmutableMap({
    operator: EQ,
    value: datePropertyName,
    property: 'hs_kpi_time_period_property'
  }), ImmutableMap({
    operator: IN,
    values: ImmutableList([kpiUnitType]),
    property: 'hs_kpi_unit_type'
  }), ImmutableMap({
    operator: IN,
    values: ImmutableList([milestone]),
    property: 'hs_milestone'
  }), ImmutableMap({
    operator: IN,
    values: ImmutableList([goalType]),
    property: 'hs_goal_type'
  })]);
  const reportCustomFilters = reportConfig.getIn(['config', 'filters', 'custom']);
  const userIdValues = Object.keys(userAssigneeIdToNameMap);
  if (userIdValues.length > 0) {
    goalCustomFilters = goalCustomFilters.push(ImmutableMap({
      operator: IN,
      values: ImmutableList(userIdValues),
      property: 'hs_assignee_user_id'
    }));
  }
  const teamIdValues = Object.keys(teamAssigneeIdToNameMap);
  if (teamIdValues.length > 0) {
    goalCustomFilters = goalCustomFilters.push(ImmutableMap({
      operator: IN,
      values: ImmutableList(teamIdValues),
      property: 'hs_assignee_team_id'
    }));
  }
  const pipelineIdValues = getValueFromCustomFilters(reportCustomFilters, 'pipeline', IN);
  if (pipelineIdValues) {
    goalCustomFilters = goalCustomFilters.push(ImmutableMap({
      operator: IN,
      values: pipelineIdValues,
      property: 'hs_pipeline_ids'
    }));
  }
  return {
    filters: ImmutableMap({
      dateRange: ImmutableMap({
        property: 'hs_end_datetime',
        useFiscalYear: reportConfig.getIn(['config', 'filters', 'dateRange', 'useFiscalYear']),
        value: reportConfig.getIn(['config', 'filters', 'dateRange', 'value'])
      }),
      custom: goalCustomFilters
    }),
    dataType: CRM_OBJECT,
    dimensions: ImmutableList(['hs_end_datetime']),
    metrics: ImmutableList([ImmutableMap({
      property: targetAmount,
      metricTypes: ImmutableList([SUM])
    })]),
    useFiscalYearInAggregation: getUseFiscalYearInAggregation(reportConfig.get('config')),
    frequency,
    objectTypeId: DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES.get(GOAL_TARGET),
    configType: TIME_SERIES,
    limit: 52,
    offset: 0,
    sort: ImmutableList([]),
    processors: isConfigReport(reportConfig) ? getReportProcessors(reportConfig) : ImmutableList([])
  };
};
export const getGoalVisualization = (report, goal) => {
  const {
    kpiUnitType,
    kpiPropertyName
  } = goal;
  const targetAmount = getTargetAmountProperty(kpiUnitType, kpiPropertyName);
  const reportMetric = report.getIn(['config', 'metrics', 0]);
  const reportMetricProperty = reportMetric.get('property');
  const reportMetricType = report.getIn(['config', 'metrics', 0, 'metricTypes', 0]);
  const reportDimension = report.getIn(['config', 'dimensions', 0]);
  const chartType = report.get('chartType');
  return XYVisualization({
    type: XY,
    style: {
      x: {
        bottom: {}
      },
      y: {
        left: {}
      },
      type: chartType,
      colors: [],
      isMobile: false,
      dataLabels: true,
      showLegend: true,
      stackLabels: false,
      dateResolution: 'DAY'
    },
    layers: [{
      x: {
        type: 'category',
        blend: 'bottom',
        field: reportDimension
      },
      y: {
        type: 'linear',
        blend: 'left',
        field: `${reportMetricType}|${reportMetricProperty}`
      },
      color: {
        defined: {
          [`${reportMetricType}|${reportMetricProperty}`]: '#fea58e'
        }
      },
      dataset: 'primary'
    }, {
      x: {
        type: 'category',
        blend: 'bottom',
        field: 'hs_end_datetime'
      },
      y: {
        type: 'linear',
        blend: 'left',
        field: `${SUM}|${targetAmount}`
      },
      mark: {
        type: chartType,
        stack: 'DEFAULT_STACK',
        stacking: true
      },
      color: {
        defined: {
          [`${SUM}|${targetAmount}`]: '#fea58e'
        }
      },
      dataset: 'secondary'
    }],
    data: {}
  });
};
export const getUniqueGoalForReportId = (goalForReportProperties, isUngatedForAdditionalObjectTypes) => {
  const {
    kpiPropertyName,
    kpiUnitType,
    milestone,
    goalType,
    assigneeType,
    datePropertyName,
    groupCorrelationUuids
  } = goalForReportProperties;
  let uniqueId = `${kpiPropertyName}_${kpiUnitType}_${milestone}_${goalType}_${assigneeType}_${datePropertyName}`;
  if (groupCorrelationUuids && isUngatedForAdditionalObjectTypes) {
    uniqueId += `_${groupCorrelationUuids.join('_')}`;
  }
  return uniqueId;
};
export function getSelectedGoalTargetId(report, goals, areDynamicGoalsSupported, areAdditionalObjectTypesSupported) {
  if (!isMultiConfigReport(report) || !areDynamicGoalsSupported) {
    return undefined;
  }
  const secondaryReportConfig = report.getIn(['reportConfigs', 'secondary']);
  const secondaryCustomFilters = getCustomFilters(secondaryReportConfig);
  const secondaryFilterGroups = getFilterGroups(secondaryReportConfig);
  const allSecondaryFilters = secondaryCustomFilters.concat(secondaryFilterGroups.flatMap(filterGroup => filterGroup.get('filters', ImmutableList()))).toList();
  if (!allSecondaryFilters) {
    return undefined;
  }
  const groupCorrelationUuid = getValueFromCustomFilters(allSecondaryFilters, 'hs_group_correlation_uuid', EQ);
  if (groupCorrelationUuid) {
    return goals.keySeq().find(goalId => {
      var _goal$groupCorrelatio;
      const goal = goals.get(goalId);
      return ((_goal$groupCorrelatio = goal.groupCorrelationUuids) === null || _goal$groupCorrelatio === void 0 ? void 0 : _goal$groupCorrelatio.includes(groupCorrelationUuid)) || false;
    });
  }
  const kpiPropertyName = getValueFromCustomFilters(allSecondaryFilters, 'hs_kpi_property_name', EQ);
  const kpiUnitType = getValueFromCustomFilters(allSecondaryFilters, 'hs_kpi_unit_type', IN, true);
  const milestone = getValueFromCustomFilters(allSecondaryFilters, 'hs_milestone', IN, true);
  const goalType = getValueFromCustomFilters(allSecondaryFilters, 'hs_goal_type', IN, true);
  const teamAssigneeFilter = getValueFromCustomFilters(allSecondaryFilters, 'hs_assignee_team_id', IN);
  const datePropertyName = getValueFromCustomFilters(allSecondaryFilters, 'hs_kpi_time_period_property', EQ);
  if (!kpiPropertyName || !kpiUnitType || !milestone || !goalType) {
    return undefined;
  }
  const goalForReportId = getUniqueGoalForReportId({
    kpiPropertyName,
    kpiUnitType,
    milestone,
    goalType,
    assigneeType: teamAssigneeFilter ? 'teams' : 'users',
    datePropertyName
  }, areAdditionalObjectTypesSupported);
  return goals.has(goalForReportId) ? goalForReportId : undefined;
}